<template>
  <div
    class="message-input px-3 pb-3"
    @dragover.prevent="onDragover"
    @dragleave="onDragleave"
    @drop.prevent="onDrop"
  >
    <!-- Client-side attachment error (e.g. oversized file) -->
    <v-alert
      v-if="fileError"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-2"
      closable
      @click:close="fileError = ''"
    >
      {{ fileError }}
    </v-alert>

    <!-- Attachment preview strip: image thumbnails and/or spreadsheet chips -->
    <div v-if="canAttach && attachedFiles.length" class="preview-strip d-flex ga-2 mb-2 flex-wrap">
      <template v-for="(item, i) in attachedFiles" :key="i">
        <div v-if="item.kind === 'image'" class="preview-thumb">
          <v-img
            :src="item.previewUrl"
            width="80"
            height="80"
            cover
            class="rounded"
          />
          <v-btn
            icon="mdi-close-circle"
            size="small"
            color="error"
            variant="flat"
            class="remove-btn"
            @click="removeFile(i)"
          />
        </div>
        <v-chip
          v-else
          class="text-none"
          variant="tonal"
          label
          closable
          prepend-icon="mdi-file-table-outline"
          @click:close="removeFile(i)"
        >
          {{ item.name }}
          <span class="text-medium-emphasis ml-1">{{ item.size }}</span>
        </v-chip>
      </template>
    </div>

    <div class="input-box" :class="{ 'drag-over': isDragging }">
      <v-textarea
        v-model="text"
        :disabled="!bridge.isReady || isStreaming || isTranscribing || chatStore.creditExhausted"
        :readonly="isRecording"
        :placeholder="chatStore.creditExhausted ? t('chat.inputPlaceholderNoCredits') : t('chat.inputPlaceholder')"
        variant="plain"
        rows="1"
        max-rows="6"
        auto-grow
        hide-details
        density="comfortable"
        class="input-textarea"
        @keydown="onKeydown"
        @paste="onPaste"
      />

      <div class="action-row d-flex align-center ga-1">
        <v-tooltip :text="t('chat.newChat')" location="top">
          <template #activator="{ props: tip }">
            <v-btn
              v-bind="tip"
              icon="mdi-chat-plus-outline"
              size="small"
              variant="text"
              :disabled="isStreaming || isRecording || isTranscribing"
              @click="newChat"
            />
          </template>
        </v-tooltip>

        <!-- Skill picker: queues a one-shot skillName for the next message and
             doubles as the active-skill indicator (driven by the
             skill_activated / skill_deactivated SSE events). -->
        <v-menu
          v-if="features.skills"
          v-model="skillMenuOpen"
          location="top start"
          :offset="4"
        >
          <template #activator="{ props: menu }">
            <v-tooltip :text="t('chat.useSkill')" location="top">
              <template #activator="{ props: tip }">
                <v-btn
                  v-bind="mergeProps(menu, tip)"
                  size="small"
                  variant="text"
                  class="skill-btn text-none"
                  :color="selectedSkillName ? 'primary' : undefined"
                  :disabled="!bridge.isReady || isStreaming || isRecording || isTranscribing"
                >
                  <v-icon size="18" start>mdi-lightning-bolt-outline</v-icon>
                  <span class="skill-btn-label">{{ selectedSkillName || t('chat.skills') }}</span>
                  <v-icon size="16" end>mdi-menu-down</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </template>

          <v-list density="compact" max-height="280" class="skill-menu">
            <v-list-item v-if="skillsLoading" disabled>
              <template #prepend>
                <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
              </template>
              <v-list-item-title class="text-body-2">{{ t('chat.loadingSkills') }}</v-list-item-title>
            </v-list-item>

            <v-list-item v-else-if="skillsError" disabled>
              <v-list-item-title class="text-body-2 text-error">{{ skillsError }}</v-list-item-title>
            </v-list-item>

            <v-list-item v-else-if="skills.length === 0" disabled>
              <v-list-item-title class="text-body-2 text-medium-emphasis">
                {{ t('chat.noSkillsAvailable') }}
              </v-list-item-title>
            </v-list-item>

            <template v-else>
              <!-- "No skill": queues a one-shot deactivation of the active
                   skill for the next message. Hidden when there's nothing to
                   deactivate or un-queue. -->
              <template v-if="chatStore.activeSkillName || chatStore.pendingSkillName || chatStore.pendingDeactivate">
                <v-list-item
                  :active="chatStore.pendingDeactivate"
                  rounded="lg"
                  @click="pickNoSkill"
                >
                  <v-list-item-title class="text-body-2 text-medium-emphasis">
                    {{ t('chat.noSkill') }}
                  </v-list-item-title>
                  <template #append>
                    <v-icon v-if="chatStore.pendingDeactivate" size="small">mdi-clock-outline</v-icon>
                  </template>
                </v-list-item>
                <v-divider class="my-1" />
              </template>

              <v-list-item
                v-for="skill in skills"
                :key="(skill.shared ? 'shared-' : '') + skill.name"
                :active="skill.name === selectedSkillName"
                rounded="lg"
                @click="pickSkill(skill)"
              >
                <v-list-item-title class="text-body-2">
                  {{ skill.name }}
                  <v-icon v-if="skill.shared" size="x-small" color="primary" class="ml-1">mdi-account-group</v-icon>
                </v-list-item-title>
                <v-list-item-subtitle v-if="skill.description" class="text-caption">
                  {{ skill.description }}
                </v-list-item-subtitle>
                <template #append>
                  <v-icon v-if="skill.name === chatStore.activeSkillName" size="small">mdi-check</v-icon>
                  <v-icon v-else-if="skill.name === chatStore.pendingSkillName" size="small">mdi-clock-outline</v-icon>
                </template>
              </v-list-item>

              <div v-if="chatStore.pendingSkillName || chatStore.pendingDeactivate" class="px-4 py-1 text-caption text-medium-emphasis">
                {{ t('chat.appliesNextMessage') }}
              </div>
            </template>
          </v-list>
        </v-menu>

        <v-spacer />

        <!-- Recording indicator -->
        <div v-if="isRecording" class="recording-indicator d-flex align-center mr-2">
          <span class="recording-dot" />
          <span class="text-caption ml-1">{{ formattedDuration }}</span>
        </div>

        <!-- Transcribing spinner -->
        <v-progress-circular
          v-if="isTranscribing"
          indeterminate
          size="20"
          width="2"
          class="mr-2"
        />

        <v-tooltip v-if="canAttach" :text="features.files ? t('chat.attachFile') : t('chat.attachImage')" location="top">
          <template #activator="{ props: tip }">
            <v-btn
              v-bind="tip"
              icon="mdi-paperclip"
              size="small"
              variant="text"
              :disabled="isStreaming || isRecording || isTranscribing"
              @click="openFilePicker"
            />
          </template>
        </v-tooltip>

        <!-- Mic / stop-recording button -->
        <template v-if="features.audio">
          <v-tooltip v-if="recorderError" :text="recorderError" location="top">
            <template #activator="{ props: tip }">
              <v-btn
                v-bind="tip"
                icon="mdi-microphone-off"
                size="small"
                color="error"
                variant="text"
                @click="toggleRecording"
              />
            </template>
          </v-tooltip>
          <v-tooltip v-else-if="!isStreaming" :text="isRecording ? t('chat.stopRecording') : t('chat.recordVoice')" location="top">
            <template #activator="{ props: tip }">
              <v-btn
                v-bind="tip"
                :icon="isRecording ? 'mdi-stop' : 'mdi-microphone'"
                size="small"
                :color="isRecording ? 'error' : undefined"
                variant="text"
                :disabled="isTranscribing"
                @click="toggleRecording"
              />
            </template>
          </v-tooltip>
        </template>

        <!-- Send / stop button -->
        <v-tooltip v-if="isStreaming" :text="t('chat.stop')" location="top">
          <template #activator="{ props: tip }">
            <v-btn
              v-bind="tip"
              icon="mdi-stop-circle"
              color="error"
              variant="tonal"
              size="small"
              @click="$emit('stop')"
            />
          </template>
        </v-tooltip>
        <v-tooltip v-else :text="t('chat.send')" location="top">
          <template #activator="{ props: tip }">
            <v-btn
              v-bind="tip"
              icon="mdi-arrow-up"
              size="small"
              color="primary"
              variant="flat"
              :disabled="!canSend"
              @click="send"
            />
          </template>
        </v-tooltip>
      </div>
    </div>

    <div v-if="chatStore.creditExhausted" class="text-caption text-medium-emphasis mt-1 px-1">
      {{ t('chat.creditsUsedUpFooter') }}
    </div>

    <input
      v-if="canAttach"
      ref="fileInput"
      type="file"
      :accept="fileAccept"
      multiple
      hidden
      @change="onFileSelected"
    >
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, computed, watch, mergeProps } from 'vue'
import { useAudioRecorder } from '../../composables/useAudioRecorder.js'
import { useBridgeStore } from '../../stores/bridge.js'
import { useChatStore } from '../../stores/chat.js'
import { useSkills } from '../../composables/useSkills.js'

const props = defineProps({
  isStreaming: { type: Boolean, default: false },
  features: { type: Object, default: () => ({}) },
})

const bridge = useBridgeStore()
const chatStore = useChatStore()

const emit = defineEmits(['send', 'stop', 'new-chat'])
const text = ref('')
const attachedFiles = ref([])
const fileInput = ref(null)
const isDragging = ref(false)
const fileError = ref('')

// Keep in sync with the backend upload cap (Uploads:MaxFileSizeBytes, 25 MB).
const MAX_FILE_SIZE = 25 * 1024 * 1024
const SPREADSHEET_EXT = /\.(xlsx|csv)$/i

// Attachments are enabled when either feature is on. The accept list and the
// paperclip tooltip adapt to whichever is enabled; with only images on, the
// behavior is byte-identical to before.
const canAttach = computed(() => props.features.images || props.features.files)
const fileAccept = computed(() => {
  const a = []
  if (props.features.images) a.push('image/*')
  if (props.features.files) a.push('.xlsx,.csv')
  return a.join(',')
})

function isSpreadsheet(file) {
  return SPREADSHEET_EXT.test(file.name || '')
    || file.type === 'text/csv'
    || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

// Skill picker. Skills can be created/edited in the drawer at any time, so
// refetch (metadata only — cheap) every time the menu opens.
const { skills, loading: skillsLoading, error: skillsError, refresh: refreshSkills } = useSkills()
const skillMenuOpen = ref(false)

// Composer indicator value: a queued pick wins over the server-confirmed
// active skill so the label previews what the next message will use. A
// queued deactivation previews as no selection (neutral "Skills" label).
const selectedSkillName = computed(
  () => (chatStore.pendingDeactivate
    ? null
    : chatStore.pendingSkillName || chatStore.activeSkillName)
)

watch(skillMenuOpen, (open) => {
  if (open) refreshSkills()
})

function pickSkill(skill) {
  chatStore.selectSkill(skill.name)
}

function pickNoSkill() {
  chatStore.selectNoSkill()
}

// Audio recorder is always instantiated for code simplicity, but if features.audio
// is false the buttons are never rendered so its methods are never called.
const {
  isRecording,
  isTranscribing,
  recordingDuration,
  error: recorderError,
  startRecording,
  stopAndTranscribe,
} = useAudioRecorder()

const canSend = computed(
  () => bridge.isReady && !isRecording.value && !isTranscribing.value
    && !chatStore.creditExhausted
    && (text.value.trim().length > 0 || attachedFiles.value.length > 0)
)

const formattedDuration = computed(() => {
  const m = Math.floor(recordingDuration.value / 60)
  const s = recordingDuration.value % 60
  return `${m}:${String(s).padStart(2, '0')}`
})

async function toggleRecording() {
  if (isRecording.value) {
    const transcribed = await stopAndTranscribe()
    if (transcribed) {
      text.value = text.value
        ? text.value + ' ' + transcribed
        : transcribed
    }
  } else {
    await startRecording()
  }
}

function openFilePicker() {
  fileInput.value?.click()
}

function newChat() {
  clearFiles()
  text.value = ''
  emit('new-chat')
}

// Route each incoming file by type: images keep the thumbnail preview,
// spreadsheets render a file chip. Both share this path (picker, drag-drop,
// paste). Spreadsheets are size-capped client-side before they can be sent.
function addFiles(fileList) {
  for (const file of fileList) {
    if (file.type && file.type.startsWith('image/')) {
      if (!props.features.images) continue
      attachedFiles.value.push({
        kind: 'image',
        file,
        previewUrl: URL.createObjectURL(file),
      })
    } else if (props.features.files && isSpreadsheet(file)) {
      if (file.size > MAX_FILE_SIZE) {
        fileError.value = t('chat.fileTooLarge', { size: '25 MB' })
        continue
      }
      attachedFiles.value.push({
        kind: 'file',
        file,
        name: file.name,
        size: humanSize(file.size),
      })
    }
  }
}

function removeFile(index) {
  const item = attachedFiles.value[index]
  if (item.kind === 'image' && item.previewUrl) URL.revokeObjectURL(item.previewUrl)
  attachedFiles.value.splice(index, 1)
  fileError.value = ''
}

function clearFiles() {
  for (const item of attachedFiles.value) {
    if (item.kind === 'image' && item.previewUrl) URL.revokeObjectURL(item.previewUrl)
  }
  attachedFiles.value = []
  fileError.value = ''
}

function onFileSelected(e) {
  addFiles(e.target.files)
  e.target.value = ''
}

function onPaste(e) {
  const items = e.clipboardData?.items
  if (!items) return
  const collected = []
  for (const item of items) {
    if (props.features.images && item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) collected.push(file)
    } else if (props.features.files && item.kind === 'file') {
      // A .xlsx/.csv copied from the OS file manager arrives as a file-kind
      // clipboard item — route it through the same addFiles as the picker.
      const file = item.getAsFile()
      if (file && isSpreadsheet(file)) collected.push(file)
    }
    // Deferred (not v1): cells copied from Excel/Sheets paste as text/plain TSV,
    // which lands harmlessly in the textarea today. An "attach as CSV" affordance
    // (TSV→CSV client-side) is a future follow-up — see SpreadsheetSandboxPlan.md 6b.
  }
  if (collected.length) addFiles(collected)
}

function onDragover() {
  if (!canAttach.value) return
  isDragging.value = true
}

function onDragleave() {
  isDragging.value = false
}

function onDrop(e) {
  isDragging.value = false
  if (!canAttach.value) return
  const files = e.dataTransfer?.files
  if (files) addFiles(files)
}

function send() {
  if (!canSend.value) return
  const files = attachedFiles.value.map(item => item.file)
  emit('send', { text: text.value, files })
  text.value = ''
  clearFiles()
}

function onKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    send()
  }
}
</script>

<style scoped>
.message-input {
  position: relative;
  background: rgb(var(--v-theme-surface));
}

.message-input::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(to bottom, rgba(var(--v-theme-surface), 0), rgb(var(--v-theme-surface)));
  pointer-events: none;
}

.v-theme--dark .message-input {
  background: rgb(var(--v-theme-background));
}

.v-theme--dark .message-input::before {
  background: linear-gradient(to bottom, rgba(var(--v-theme-background), 0), rgb(var(--v-theme-background)));
}

.preview-strip {
  padding-left: 4px;
}

.preview-thumb {
  position: relative;
  line-height: 0;
}

.input-box {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 4px 8px 4px 12px;
  background: rgb(var(--v-theme-surface));
  transition: border-color 0.15s ease;
}

.input-box:focus-within {
  border-color: rgba(var(--v-border-color), calc(var(--v-border-opacity) * 2));
}

.input-textarea :deep(.v-field) {
  padding: 0;
  background: transparent;
  box-shadow: none;
}

.input-textarea :deep(.v-field__field) {
  background: transparent;
}

.input-textarea :deep(.v-field__input) {
  padding-top: 8px;
  padding-bottom: 4px;
  mask-image: none;
  -webkit-mask-image: none;
  font-size: 0.875rem;
}

.input-textarea :deep(textarea) {
  font-size: 0.875rem;
}

.input-textarea :deep(.v-field__outline),
.input-textarea :deep(.v-field__overlay),
.input-textarea :deep(.v-field__loader) {
  display: none;
}

.action-row {
  padding: 2px 0 2px 0;
}

.skill-btn {
  letter-spacing: normal;
}

.skill-btn-label {
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
}

.skill-menu {
  min-width: 220px;
  max-width: 320px;
}

.remove-btn {
  position: absolute;
  top: -6px;
  right: -6px;
}

.drag-over {
  outline: 2px dashed rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

.recording-indicator {
  white-space: nowrap;
}

.recording-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-error));
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
</style>
